import React, { useCallback, useEffect, useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { AlertTriangle, CheckCircle2, ExternalLink, Info, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  get_recording_os_permissions,
  open_recording_os_permission_settings,
  request_recording_os_permission,
} from "../api";
import type {
  RecordingOsPermissionItem,
  RecordingOsPermissionKind,
  RecordingOsPermissionsPayload,
} from "../api/types";
import Modal from "./Modal";

type RecordingOsPermissionsModalProps = {
  open: boolean;
  onClose: () => void;
  onShowCapturePrivacy: () => void;
  onStatus?: (status: RecordingOsPermissionsPayload) => void;
};

const permissionLabelKey = (kind: RecordingOsPermissionKind) => {
  switch (kind) {
    case "accessibility":
      return "accessibility_permission";
    case "input_monitoring":
      return "input_monitoring_permission";
    case "screen_and_system_audio_recording":
      return "screen_and_system_audio_recording_permission";
    case "desktop_accessibility":
      return "desktop_accessibility_permission";
  }
};

const permissionDescriptionKey = (kind: RecordingOsPermissionKind) => {
  switch (kind) {
    case "input_monitoring":
      return "input_monitoring_blocked_description";
    case "desktop_accessibility":
      return "desktop_accessibility_description";
    case "accessibility":
    case "screen_and_system_audio_recording":
      return null;
  }
};

/** The ways the modal offers to fix a missing permission. */
const permissionFixes = (kind: RecordingOsPermissionKind) => {
  switch (kind) {
    case "accessibility":
    case "screen_and_system_audio_recording":
      return { requestLabelKey: "request_permission", openSettings: true } as const;
    case "input_monitoring":
      // a request can't undo an explicit denial, only System Settings can
      return { requestLabelKey: null, openSettings: true } as const;
    case "desktop_accessibility":
      // desktops have no settings for the switch itself
      return { requestLabelKey: "turn_on_permission", openSettings: false } as const;
  }
};

const RecordingOsPermissionsModal = ({
  open,
  onClose,
  onShowCapturePrivacy,
  onStatus,
}: RecordingOsPermissionsModalProps) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<RecordingOsPermissionsPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Bumped by every fetch and action so a slow response cannot overwrite a
  // newer status.
  const generation = useRef(0);

  const permissions = status?.permissions ?? [];
  const isReady = status !== null && permissions.every(permission => permission.granted);
  const requiredMissing = permissions.some(
    permission => permission.required && !permission.granted,
  );
  const actionsDisabled = loading || busy;
  const accessibilityGranted = permissions.some(
    permission => permission.kind === "accessibility" && permission.granted,
  );
  const visiblePermissions = permissions.filter(
    permission =>
      permission.kind !== "input_monitoring" || (accessibilityGranted && !permission.granted),
  );

  const applyStatus = useCallback(
    (nextStatus: RecordingOsPermissionsPayload) => {
      setStatus(nextStatus);
      onStatus?.(nextStatus);
    },
    [onStatus],
  );

  /**
   * Explicit loads (open, "check again") show the loading state and clear the
   * error; a silent refresh (window focus) leaves both alone so an error the
   * user has not read yet survives and the buttons do not flicker.
   */
  const loadPermissions = useCallback(
    async ({ silent = false } = {}) => {
      const requestGeneration = ++generation.current;
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      try {
        const nextStatus = await get_recording_os_permissions();
        if (requestGeneration === generation.current) {
          applyStatus(nextStatus);
        }
      } catch (loadError: unknown) {
        if (!silent) {
          setError(
            loadError instanceof Error ? loadError.message : t("recording_permissions_load_failed"),
          );
        }
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [applyStatus, t],
  );

  useEffect(() => {
    if (open) {
      void loadPermissions();
    }
  }, [loadPermissions, open]);

  // Pick up changes made in System Settings when the user comes back; not
  // while an action is running, since its own result is about to land.
  useEffect(() => {
    if (!open || busy) {
      return;
    }

    const refreshOnFocus = () => {
      void loadPermissions({ silent: true });
    };
    window.addEventListener("focus", refreshOnFocus);
    return () => {
      window.removeEventListener("focus", refreshOnFocus);
    };
  }, [busy, loadPermissions, open]);

  const handlePermissionAction = async (
    permission: RecordingOsPermissionItem,
    action: "request" | "settings",
  ) => {
    setBusy(true);
    setError(null);
    generation.current += 1;
    try {
      const nextStatus =
        action === "request"
          ? await request_recording_os_permission(permission.kind)
          : await open_recording_os_permission_settings(permission.kind);
      applyStatus(nextStatus);
    } catch (actionError: unknown) {
      setError(actionError instanceof Error ? actionError.message : String(actionError));
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return null;
  }

  return (
    <Modal open={open} onOpenChange={nextOpen => !nextOpen && onClose()} size="lg">
      <div className="pr-10">
        <Dialog.Title className="text-lg font-semibold text-text">
          {t("recording_permissions")}
        </Dialog.Title>
        <Dialog.Description className="mt-2 text-sm leading-6 text-text-mid">
          {requiredMissing
            ? t("recording_permissions_modal_description")
            : isReady
              ? t("recording_permissions_ready_description")
              : t("recording_permissions_recommended_description")}
        </Dialog.Description>
      </div>

      {requiredMissing && (
        <div className="mt-4 flex gap-3 rounded-lg border border-warning/40 bg-warning/10 p-3 text-warning-strong">
          <AlertTriangle aria-hidden="true" size={18} className="mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold">{t("recording_permissions_restart_title")}</p>
            <p className="mt-1 text-xs leading-5">{t("recording_permissions_restart_app")}</p>
          </div>
        </div>
      )}

      {error && (
        <div className="mt-4 rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-xs text-danger">
          {error}
        </div>
      )}

      <div className="mt-5 overflow-hidden rounded-lg border border-outline divide-y divide-outline">
        {loading && !status && (
          <p className="px-4 py-3.5 text-sm text-text-dim">{t("loading_ellipsis")}</p>
        )}
        {visiblePermissions.map(permission => {
          const descriptionKey = permissionDescriptionKey(permission.kind);
          const { requestLabelKey, openSettings } = permissionFixes(permission.kind);
          return (
            <div
              key={permission.kind}
              className="grid gap-3 px-4 py-3.5 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center"
            >
              <div className="min-w-0">
                <p className="break-words text-sm font-medium text-text">
                  {t(permissionLabelKey(permission.kind))}
                </p>
                {descriptionKey && (
                  <p className="mt-1 max-w-sm text-xs leading-5 text-text-mid">
                    {t(descriptionKey)}
                  </p>
                )}
              </div>
              {permission.granted ? (
                <span className="inline-flex shrink-0 items-center gap-1.5 text-xs font-medium text-success">
                  <CheckCircle2 aria-hidden="true" size={14} />
                  {t("permission_status_granted")}
                </span>
              ) : (
                <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                  {requestLabelKey && (
                    <button
                      type="button"
                      disabled={actionsDisabled}
                      onClick={() => {
                        void handlePermissionAction(permission, "request");
                      }}
                      className="min-h-9 rounded-lg border border-outline bg-surface px-3 text-xs font-medium text-text transition hover:bg-muted-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {t(requestLabelKey)}
                    </button>
                  )}
                  {requestLabelKey && openSettings && (
                    <span className="text-xs text-text-dim">{t("or")}</span>
                  )}
                  {openSettings && (
                    <button
                      type="button"
                      disabled={actionsDisabled}
                      onClick={() => {
                        void handlePermissionAction(permission, "settings");
                      }}
                      className="inline-flex min-h-9 items-center gap-2 rounded-lg border border-outline bg-surface px-3 text-xs font-medium text-text transition hover:bg-muted-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <ExternalLink aria-hidden="true" size={14} />
                      {t("open_system_settings")}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={() => {
          onClose();
          onShowCapturePrivacy();
        }}
        className="mt-4 inline-flex items-center gap-1.5 rounded text-sm font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        <Info aria-hidden="true" size={14} className="shrink-0" />
        {t("what_platonic_records")}
      </button>

      <div className="mt-5 flex flex-wrap justify-end gap-2">
        {!isReady && (
          <button
            type="button"
            disabled={actionsDisabled}
            onClick={() => {
              void loadPermissions();
            }}
            className="inline-flex min-h-10 items-center gap-2 rounded-lg border border-outline bg-surface px-3 text-sm font-medium text-text transition hover:bg-muted-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
          >
            <RefreshCw aria-hidden="true" size={15} />
            {t("recording_permissions_check_again")}
          </button>
        )}
        <Dialog.Close asChild>
          <button
            type="button"
            className={`min-h-10 rounded-lg px-4 text-sm font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
              isReady
                ? "bg-primary text-on-accent hover:bg-primary/90"
                : "border border-outline bg-surface text-text hover:bg-muted-surface"
            }`}
          >
            {t("close")}
          </button>
        </Dialog.Close>
      </div>
    </Modal>
  );
};

export default RecordingOsPermissionsModal;
